import { type JSX, useEffect, useState } from 'react';
import { generatePath, useLocation, useParams } from 'react-router';

import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { TextToast } from 'Components/Toast/Toast';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { UCRM_BUSINESSES_AND_USE_CASES_LISTING, UCRM_USE_CASE_DASHBOARD } from 'Config/PathTemplates';
import { UCRM_SUCCESS_MESSAGE_BUSINESS } from 'Config/Text';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { UCRM_ROLES } from 'Helpers/UserUtils';
import { BusinessForNonUcrmUser, BusinessResponse } from 'Models/Ucrm';
import { SEARCH_PARAM_USE_CASE_DASHBOARD_ORIGIN_USE_CASE_ID, SEARCH_PARAM_USE_CASE_DASHBOARD_ORIGIN_USE_CASE_NAME } from 'Pages/UCRM/UseCaseDashboard/UseCaseDashboard';

import { ManageBusinessFormFields } from './ManageBusinessFormFields/ManageBusinessFormFields';

interface UrlPathParams {
    thirdPartyId: string;
}

export interface ManageBusinessProps {
    authHandler: AuthHandler;
    exceptionsApi: ExceptionsApi;
    issuesApi: IssuesApi;
    ucrmApi: ClientUcrmApi;
}

/** Allows the user to update or delete an existing business. Note that businesses are created via the `UcrmIntake` component, not via this component. */
export const ManageBusiness = (props: ManageBusinessProps): JSX.Element => {
    const { thirdPartyId } = useParams<keyof UrlPathParams>() as UrlPathParams;

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    /** Present in the URL if and only if the user navigated here from the Use Case Dashboard page. */
    const useCaseDashboardOriginUseCaseId = searchParams.get(SEARCH_PARAM_USE_CASE_DASHBOARD_ORIGIN_USE_CASE_ID);
    /** Present in the URL if and only if the user navigated here from the Use Case Dashboard page. */
    const useCaseDashboardOriginUseCaseName = searchParams.get(SEARCH_PARAM_USE_CASE_DASHBOARD_ORIGIN_USE_CASE_NAME);

    const userRoles = props.authHandler.getUserRoles();
    const userHasFullUcrmAccess = userHasAuthorizedRole(userRoles, UCRM_ROLES);

    const [toastContent, setToastContent] = useState<{ variant: 'success' | 'failure'; text: string }>();
    const [business, setBusiness] = useState<BusinessResponse | BusinessForNonUcrmUser>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState<boolean>(false);

    useEffect(() => {
        const getBusiness = async (): Promise<void> => {
            try {
                setBusiness((userHasFullUcrmAccess ? await props.ucrmApi.getBusinessDetails(thirdPartyId) : await props.ucrmApi.getBusinessDetailsForNonUcrmUser(thirdPartyId)).data);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        getBusiness();
    }, [props.ucrmApi, thirdPartyId, userHasFullUcrmAccess]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (business === undefined) {
        return <Placeholder />;
    }

    const headerTitle = 'Manage Business';

    return (
        <>
            {toastContent && <TextToast clearToast={() => setToastContent(undefined)} {...toastContent} />}
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(UCRM_BUSINESSES_AND_USE_CASES_LISTING)}>Businesses and Use Cases</BreadcrumbLink>
                        {useCaseDashboardOriginUseCaseId && useCaseDashboardOriginUseCaseName && <BreadcrumbLink link={generatePath(UCRM_USE_CASE_DASHBOARD, { third_party_id: thirdPartyId, service_id: useCaseDashboardOriginUseCaseId })}>{`${business.name} - ${useCaseDashboardOriginUseCaseName}`}</BreadcrumbLink>}
                        <BreadcrumbText>{headerTitle}</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle={headerTitle}
                body={[
                    {
                        content: <ManageBusinessFormFields exceptionsApi={props.exceptionsApi} issuesApi={props.issuesApi} business={business} ucrmApi={props.ucrmApi} userHasFullUcrmAccess={userHasFullUcrmAccess} onFormSubmitBegin={() => setToastContent(undefined)} onFormSubmitFailed={(text) => setToastContent({ variant: 'failure', text })} onFormSubmittedSuccessfully={async () => setToastContent({ variant: 'success', text: UCRM_SUCCESS_MESSAGE_BUSINESS })} />,
                    },
                ]}
            />
        </>
    );
};
